import { Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Logger } from 'aws-amplify';
import clsx from 'clsx';
import { Resizable, ResizeCallback } from 're-resizable';
import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Draggable, {
  DraggableData,
  DraggableEvent,
  DraggableEventHandler,
} from 'react-draggable';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import {
  EmailIcon,
  LinkIcon,
  LocationIcon,
  NoCompanyIcon,
  NumberIcon,
  PhoneIcon,
  SignatureCheckboxIcon,
  SignatureCheckboxUncheckedIcon,
  SignatureDateIcon,
  SignatureIcon,
  SignatureInitialIcon,
  SignatureTextIcon,
  TrashIcon,
} from 'src/components/icons';
import {
  CHECKBOX_CHECKED_VALUE,
  CONTRACT_COMPONENT_DEFAULT_HEIGHT,
  CONTRACT_COMPONENT_DEFAULT_WIDTH,
  CONTRACT_COMPONENT_MINIMUM_WIDTH,
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SignatureComponentType,
} from 'src/constants';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { PredefinedAutofillKeys } from 'src/entities/Contract';
import { useContractDateFormat } from 'src/hooks/useContractDateFormat';
import { FieldType } from 'src/services/api/contractsApi';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import {
  CustomFieldEntityType,
  CustomFieldType,
} from 'src/store/clients/types';
import { RootState } from 'src/store/reduxTypes';
import { SignaturePageComponent } from 'src/store/signaturePage/types';
import { BlackHeadings, NonHoverBorder, red, white } from 'src/theme/colors';
import { RegularCardShadow } from 'src/theme/shadows';
import { zIndex } from 'src/theme/zindex';
import ColorUtils from 'src/utils/ColorUtils';
import { ensureUnreachable } from 'src/utils/common_utils';
import { parseMessageToHTML } from 'src/utils/UrlUtils';

import {
  SIGNATURE_FONT,
  SIGNATURE_FONT_SIZE,
} from '../Canvas/canvas_constants';
import { convertTextToDataEncodedImage } from '../Canvas/canvas_helpers';
import { isRequestComponent } from '../Contracts/contract_helpers';
import { parseDateValue } from '../UI/Date/useAddDateForm';

import { ISize } from './SignatureDraggableComponent';

const logger = new Logger('SignatureDraggableItem');

type StyleProps = {
  pageScalerFactor: number; // Represents the ratio of width of current screen to signaturePageContentWidth
  isSmallScreen: boolean;
  isMounted: boolean;
  isClient: boolean;
  component: SignaturePageComponent;
};

const ICON_SIZE = 14;
const CHECKBOX_ICON_SIZE = 16;
const CONTRACT_DEFAULT_FONT_SIZE = 12;
const MIN_FONT_SIZE = 1;

// Binary-search the largest font size (down to MIN_FONT_SIZE) at which the
// element's wrapped content fits its container on both axes. textfit was
// previously used here, but its alignVert post-processing changed the element
// layout after measurement, causing the chosen font size to overflow on render.
const fitTextToContainer = (el: HTMLElement, maxFontSize: number) => {
  const containerHeight = el.clientHeight;
  const containerWidth = el.clientWidth;
  if (!containerHeight || !containerWidth) return;

  let low = MIN_FONT_SIZE;
  let high = Math.floor(maxFontSize);
  let bestSize = low;

  while (low <= high) {
    const mid = (low + high) >> 1;
    el.style.fontSize = `${mid}px`;
    // Width is checked alongside height so an unbreakable token (long email,
    // URL) shrinks to fit a narrow box instead of clipping at the edge.
    if (
      el.scrollHeight <= containerHeight &&
      el.scrollWidth <= containerWidth
    ) {
      bestSize = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  el.style.fontSize = `${bestSize}px`;
};

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  root: {
    display: 'flex',
    position: 'absolute' as const,
    cursor: 'default',
    zIndex: theme.zIndex.contractSignatureComponent,
  },
  selectedContractButton: {
    // Display the selected signature button above non-selected ones.
    // Ensures proper functionality, like delete and resize, in cases of overlap
    zIndex: theme.zIndex.contractSignatureComponent + 1,
  },
  closeIcon: {
    height: SIGNATURE_COMPONENT_DEFAULT_HEIGHT, // if close icon and button height is not same then we get minor shift in the component when placed on the PDF
    width: SIGNATURE_COMPONENT_DEFAULT_HEIGHT, // icon is square so width and height should be same
    backgroundColor: white,
    border: `1px solid ${NonHoverBorder}`,
    boxShadow: RegularCardShadow,
    marginLeft: theme.spacing(1),
    '&:hover': {
      backgroundColor: white,
    },
  },
  signatureImage: {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain' as const,
    width: 'auto',
    height: '100%',
  },
  eSigComponentButton: {
    position: 'absolute' as const,
    height: '100%',
    width: '100%',
    minWidth: CONTRACT_COMPONENT_MINIMUM_WIDTH,
    borderRadius: 0,
    textAlign: 'left' as const,
    fontSize: CONTRACT_DEFAULT_FONT_SIZE,
    zIndex: theme.zIndex.contractSignatureComponent,
    justifyContent: 'flex-start',
    alignItems: 'center',
    lineHeight: 1.75,
    '& .MuiButton-iconSizeMedium > svg': {
      width:
        props.isMounted && props.isSmallScreen
          ? ICON_SIZE / props.pageScalerFactor
          : ICON_SIZE,
      height:
        props.isMounted && props.isSmallScreen
          ? ICON_SIZE / props.pageScalerFactor
          : ICON_SIZE,
    },
  },
  eSigComponentValue: {
    color: BlackHeadings,
    backgroundColor: 'transparent',
  },
  eSigComponentPlaceholder: {
    borderRadius: `${theme.shape.borderRadius}px`,
    color: theme.palette.primary.main,
    backgroundColor: ColorUtils.GetColorDarknessShades(
      theme.palette.primary.main,
    ).light,
    '&:hover': {
      backgroundColor: ColorUtils.GetColorDarknessShades(
        theme.palette.primary.main,
      ).light,
    },
  },
  contractButtonIcon: {
    marginLeft: theme.spacing(1),
    marginRight: '6px',
  },
  autoFitText: {
    display: 'inline-block',
    height: '100%',
    lineHeight: 'normal',
    overflow: 'hidden',
    // Wrap onto multiple lines and break unbreakable tokens so the binary-search
    // font fit shrinks a fully-wrapped block rather than clipping overflow.
    wordWrap: 'break-word',
    overflowWrap: 'anywhere',
  },
  clientPlaceholderText: {
    display: 'inherit',
    alignItems: 'center',
  },
  clientContractPlaceholderText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingRight: theme.spacing(1),
  },
  signatureComponent: {
    height: props.component.height,
    width: props.component.width,
    fontSize: props.isSmallScreen
      ? `${CONTRACT_DEFAULT_FONT_SIZE / props.pageScalerFactor}px !important`
      : `${CONTRACT_DEFAULT_FONT_SIZE}px !important`,
  },
  signatureButtonHover: {
    '&.MuiButton-root:hover': {
      backgroundColor: props.isClient ? 'transparent' : 'inherit',
    },
  },
  // class to overide default MUI button cursor ( pointer )
  signatureButtonCursor: {
    cursor: 'default',
  },
  startIcon: {
    margin: 0,
  },
}));

export enum ContractDragItemType {
  Pending = 'pending',
  Edit = 'edit',
}

type PendingDraggableItemProps = {
  type: ContractDragItemType.Pending;
  component: SignaturePageComponent;
  disableDragging: boolean;
};

type ActiveDraggableItemProps = {
  type: ContractDragItemType.Edit;
  component: SignaturePageComponent;
  handleComponentMoved: (e: DraggableEvent, data: DraggableData) => void;
  handleComponentResized: (dimensions: ISize, componentId: string) => void;
  handleRemoveComponent: () => void;
  handleComponentClicked: MouseEventHandler<HTMLButtonElement>;
  isComponentSelected: boolean;
  disableDragging: boolean;
};

export type ContractDraggableItemProps =
  | PendingDraggableItemProps
  | ActiveDraggableItemProps;

const ContractDraggableItemBase = React.forwardRef<
  HTMLButtonElement,
  ContractDraggableItemProps
>((props, draggableButtonRef) => {
  const { component, disableDragging } = props;
  const isComponentSelected =
    'isComponentSelected' in props && props.isComponentSelected;
  const activeComponentNodeRef = React.useRef<HTMLButtonElement>(null);
  const dragNodeRef = React.useRef<HTMLDivElement>(null);

  const [isResizing, setIsResizing] = React.useState(false);
  // State to determine whether to lock the aspect ratio during resizing when the shift key is pressed
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isMounted, toggleMountedState] = React.useReducer(
    (state) => !state,
    false,
  ); // textFit needs the component to be mounted to work

  const { pageScalerFactor } = useContext(ContractBuilderContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const isActiveComponent = useSelector(
    (state: RootState) =>
      state.signaturePage.activeComponentKey === component.key,
  );
  const customFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const dateFormat = useContractDateFormat();

  const { classes } = useStyles({
    isSmallScreen,
    pageScalerFactor,
    isMounted,
    isClient,
    component,
  });
  const [textNode, setTextNode] = React.useState<HTMLSpanElement | null>(null);
  const [size, setSize] = React.useState<ISize>({
    height: component?.height ?? CONTRACT_COMPONENT_DEFAULT_HEIGHT,
    width: component?.width ?? CONTRACT_COMPONENT_DEFAULT_WIDTH,
  });

  const pdfContainer = document.getElementById(
    'pdf-container',
  ) as HTMLDivElement;

  useEffect(() => {
    toggleMountedState();
    return () => {
      toggleMountedState();
    };
  }, []);

  const renderContractComponentValue = useMemo(() => {
    const isCheckboxComponent =
      component.componentType === SignatureComponentType.REQUEST_CHECKBOX ||
      component.componentType === SignatureComponentType.OWNER_CHECKBOX;

    // Checkbox renders its own box + label for every state (placed, My-set, or
    // client-toggled), so it bypasses the image/placeholder/text branches below.
    if (isCheckboxComponent) {
      const isChecked = component.value === CHECKBOX_CHECKED_VALUE;
      // Only `label` is a user-facing caption for checkboxes; `name` holds the
      // field's state ("checked"/"unchecked") after save, so it must not be used
      // as a fallback or a label-less checkbox prints the word "unchecked".
      const checkboxLabel = component.label || '';
      // Scale the box with the page on small screens (matching the other field
      // icons) so it stays in proportion to the label under the mobile scale-down.
      const checkboxIconSize =
        isMounted && isSmallScreen
          ? CHECKBOX_ICON_SIZE / pageScalerFactor
          : CHECKBOX_ICON_SIZE;
      const CheckboxFieldIcon = isChecked
        ? SignatureCheckboxIcon
        : SignatureCheckboxUncheckedIcon;
      return (
        <span
          role="checkbox"
          aria-checked={isChecked}
          className="flex h-full w-full items-center gap-1 overflow-hidden"
        >
          {/* Inline width/height override MUI SvgIcon's default 1em size; ml-2
              matches the left offset of the other field-type icons (contractButtonIcon). */}
          <CheckboxFieldIcon
            className="ml-2 shrink-0"
            style={{ width: checkboxIconSize, height: checkboxIconSize }}
          />
          {checkboxLabel ? (
            // truncate = nowrap + hidden overflow + ellipsis; pr-2 keeps the
            // ellipsis off the tile's right border.
            <span className="truncate pr-2">{checkboxLabel}</span>
          ) : null}
        </span>
      );
    }

    const isImageComponent = [
      SignatureComponentType.REQUEST_SIGN,
      SignatureComponentType.REQUEST_INITIAL,
      SignatureComponentType.OWNER_SIGN,
      SignatureComponentType.OWNER_INITIAL,
    ].includes(component.componentType);

    if (!component.imageTextValue) {
      console.info('No image text value found for component', component);

      // migrated contracts will only have an encodedImgValue, so confirm we can parse that value and return here if necessary
      if (
        isImageComponent &&
        component.encodedImgValue?.startsWith('data:image')
      ) {
        return (
          <img
            src={component.encodedImgValue}
            alt="signatureImage"
            height={component.resizedHeight}
            width={component.resizedWidth}
            draggable={false}
            className={classes.signatureImage}
          />
        );
      }
    }

    const imgSrc = isImageComponent
      ? convertTextToDataEncodedImage(
          component.imageTextValue || '',
          SIGNATURE_FONT_SIZE,
          SIGNATURE_FONT,
        )
      : '';

    // If component width is too small to fit the text label
    // Don't show the text label
    if (
      !component.value &&
      component.width <= CONTRACT_COMPONENT_MINIMUM_WIDTH + 8
    ) {
      return null;
    }

    // For request components
    if (!component.value && isRequestComponent(component.componentType)) {
      return (
        <span
          className={classes.clientContractPlaceholderText}
          style={{
            width: `calc(100% - ${CONTRACT_COMPONENT_MINIMUM_WIDTH}px)`,
          }}
        >
          {component.name || component.label}
        </span>
      );
    }

    if (isImageComponent && imgSrc) {
      return (
        <img
          src={imgSrc}
          alt="signatureImage"
          height={component.resizedHeight}
          width={component.resizedWidth}
          draggable={false}
          className={classes.signatureImage}
        />
      );
    }

    const isDateComponent =
      component.componentType === SignatureComponentType.OWNER_DATE ||
      component.componentType === SignatureComponentType.REQUEST_DATE ||
      component.autoFillField === PredefinedAutofillKeys.ShareDate;
    let displayValue = component.value || '';
    if (isDateComponent && displayValue) {
      const parsed = parseDateValue(displayValue, dateFormat);
      if (parsed) displayValue = parsed.format(dateFormat);
    }

    // For text-based components
    // If the input supports multiline content, like variable inputs,
    // it can contain HTML content. We need to parse the content to HTML.
    const parsedValue = parseMessageToHTML(displayValue);

    return (
      <span
        ref={setTextNode}
        className={classes.autoFitText}
        style={{ width: '100%' }}
        dangerouslySetInnerHTML={{ __html: parsedValue }}
      />
    );
  }, [
    component,
    classes.autoFitText,
    classes.signatureImage,
    classes.clientContractPlaceholderText,
    dateFormat,
    isMounted,
    isSmallScreen,
    pageScalerFactor,
  ]);

  // based on the component type render the signature icon
  // need to show icon only for client inputs
  const renderSignatureIcon = useMemo(() => {
    if (component.value) {
      return null;
    }

    const iconClasses = clsx(classes.contractButtonIcon);

    // Derive autofill field type from predefined key or custom field ID
    const getAutofillFieldType = ():
      | FieldType
      | CustomFieldType
      | 'email'
      | 'company' => {
      const { autoFillField } = component;
      if (!autoFillField) return FieldType.Text;

      // Check predefined keys - email is the only non-text predefined field
      if (autoFillField === PredefinedAutofillKeys.Email) return 'email';
      if (autoFillField === PredefinedAutofillKeys.Company) return 'company';
      if (
        autoFillField === PredefinedAutofillKeys.FirstName ||
        autoFillField === PredefinedAutofillKeys.LastName ||
        autoFillField === PredefinedAutofillKeys.FullName
      ) {
        return FieldType.Text;
      }

      // For custom fields, look up the type by ID
      const customField = customFields[autoFillField];
      return customField?.type ?? FieldType.Text;
    };

    switch (component.componentType) {
      case SignatureComponentType.REQUEST_SIGN:
        return <SignatureIcon className={iconClasses} />;
      case SignatureComponentType.REQUEST_INITIAL:
        return <SignatureInitialIcon className={iconClasses} />;
      case SignatureComponentType.REQUEST_DATE:
        return <SignatureDateIcon className={iconClasses} />;
      case SignatureComponentType.REQUEST_TEXT: {
        const fieldType = getAutofillFieldType();
        switch (fieldType) {
          case 'company':
            return <NoCompanyIcon className={iconClasses} />;
          case 'email':
            return <EmailIcon className={iconClasses} />;
          case 'phoneNumber':
            return <PhoneIcon className={iconClasses} />;
          case 'url':
            return <LinkIcon className={iconClasses} />;
          case 'address':
            return <LocationIcon className={iconClasses} />;
          case 'number':
            return <NumberIcon className={iconClasses} />;
          case 'text':
          default:
            return <SignatureTextIcon className={iconClasses} />;
        }
      }
      // Owner inputs show no field-type icon. Checkbox (request or owner) also
      // returns null here because it draws its own box + label in
      // renderContractComponentValue — rendering an icon too produced a second box.
      case SignatureComponentType.OWNER_DATE:
      case SignatureComponentType.OWNER_INITIAL:
      case SignatureComponentType.OWNER_SIGN:
      case SignatureComponentType.OWNER_TEXT:
      case SignatureComponentType.REQUEST_CHECKBOX:
      case SignatureComponentType.OWNER_CHECKBOX:
        return null;
      default:
        return ensureUnreachable(component.componentType);
    }
  }, [
    component.value,
    component.componentType,
    component.autoFillField,
    customFields,
    classes,
  ]);

  const calculateFontSize = React.useCallback(() => {
    if (!textNode) return;
    const maxFontSize = isSmallScreen
      ? CONTRACT_DEFAULT_FONT_SIZE / pageScalerFactor
      : CONTRACT_DEFAULT_FONT_SIZE;
    try {
      fitTextToContainer(textNode, maxFontSize);
    } catch (e) {
      logger.error(e, 'Error while calculating field');
    }
  }, [textNode, isSmallScreen, pageScalerFactor]);

  // Fit text to container whenever the span attaches, the value changes, or the container is resized.
  // useLayoutEffect runs synchronously after DOM mutations but before paint, so dimensions are available.
  // If layout hasn't settled (clientHeight=0), retry on the next animation frame.
  React.useLayoutEffect(() => {
    if (!textNode) return undefined;
    calculateFontSize();
    if (textNode.clientHeight && textNode.clientWidth) return undefined;
    const raf = requestAnimationFrame(() => calculateFontSize());
    return () => cancelAnimationFrame(raf);
  }, [calculateFontSize, component.value, size, textNode]);

  React.useEffect(() => {
    if (size.height === component.height && size.width === component.width) {
      return;
    }

    setSize({
      height: component?.height, // default height: ;
      width: component?.width, // default width: ;
    });
  }, [component, size]);

  // DraggableEventHandler is used to update the component position
  // If false is returned, the component is not dragged
  const handleDisableDrag: DraggableEventHandler = () => false;

  const handleResizeStop: ResizeCallback = (_, __, ref) => {
    const dimensions = {
      height: ref.clientHeight,
      width: ref.clientWidth,
    };

    // if the component is resized then fit the text content to the new resized size
    if (textNode) {
      const maxFontSize = isSmallScreen
        ? CONTRACT_DEFAULT_FONT_SIZE / pageScalerFactor
        : CONTRACT_DEFAULT_FONT_SIZE;
      fitTextToContainer(textNode, maxFontSize);
    }

    if ('handleComponentResized' in props) {
      props.handleComponentResized(dimensions, component.key);
    }
    setIsResizing(false);
  };

  const handleResize: ResizeCallback = (
    event,
    _ususedDirection,
    _unusedRef,
    d,
  ) => {
    // This will be true when shiftKey is pressed while resizing
    const shiftPressed = event.shiftKey;
    setLockAspectRatio(shiftPressed);

    setSize((prev) => ({
      height: prev.height + d.height,
      width: prev.width + d.width,
    }));
  };

  const handleDrag: DraggableEventHandler = (_, data) => {
    // if component is not moved from it's last position don't set dragging to true
    if (data.x === component.xPosition && data.y === component.yPosition)
      return;

    setIsDragging(true);
  };

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    // if component is not moved from it's last position don't update reducer state
    if (data.x === component.xPosition && data.y === component.yPosition)
      return;

    if (!isResizing && 'handleComponentMoved' in props) {
      props.handleComponentMoved(e, data);
    }
    setIsDragging(false);
  };

  const handleRemoveComponent = () => {
    if ('handleRemoveComponent' in props) {
      props.handleRemoveComponent();
    }
  };

  const handleComponentClicked: MouseEventHandler<HTMLButtonElement> = (e) => {
    if ('handleComponentClicked' in props) {
      props.handleComponentClicked(e);
    }
  };

  const renderRemoveIcon = () => {
    // Don't show remove icon to client
    if (isClient) return null;

    // Don't show remove icon if component is not selected
    if (!isComponentSelected) return null;

    // Don't show remove icon if dragging, so that user can place signature component on the edge of page
    if (isDragging) return null;

    // Don't show remove icon if resizing
    if (isResizing) return null;

    return (
      <IconButton onClick={handleRemoveComponent} className={classes.closeIcon}>
        <TrashIcon
          data-testid="remove-button"
          style={{ fontSize: 16, color: red }}
        />
      </IconButton>
    );
  };

  // Indicator for current signing componenet only for client
  // if active componets key is same as component key show empty button with white background
  if (isActiveComponent && isClient)
    return (
      <Draggable
        nodeRef={activeComponentNodeRef}
        defaultClassName={classes.root}
        defaultPosition={{ x: component.xPosition, y: component.yPosition }}
        disabled
      >
        <Button
          ref={activeComponentNodeRef}
          id={component.key}
          data-testid="active-signature-component"
          className={clsx(classes.eSigComponentButton, {
            [classes.signatureComponent]: isMounted,
          })}
          style={{
            border: '1px solid',
          }}
        />
      </Draggable>
    );

  return (
    <Draggable
      nodeRef={dragNodeRef}
      bounds="parent"
      defaultClassName={clsx(classes.root, {
        [classes.selectedContractButton]: !isClient && isComponentSelected,
      })}
      onStop={handleDragStop}
      position={{ x: component.xPosition, y: component.yPosition }}
      disabled={disableDragging}
      onDrag={isResizing ? handleDisableDrag : handleDrag} // is user is resizing then disable drag
    >
      <div ref={dragNodeRef}>
        <Resizable
          lockAspectRatio={lockAspectRatio}
          onResizeStart={() => setIsResizing(true)}
          size={size}
          onResizeStop={handleResizeStop}
          minWidth={CONTRACT_COMPONENT_MINIMUM_WIDTH}
          minHeight={CONTRACT_COMPONENT_DEFAULT_HEIGHT / pageScalerFactor}
          enable={{
            top: false,
            left: false,
            right: false,
            bottom: false,
            bottomLeft: false,
            topLeft: false,
            topRight: false,
            bottomRight: !isClient && isComponentSelected,
          }}
          bounds={pdfContainer}
          onResize={handleResize}
          // styles for the sqaure resize handler
          handleStyles={{
            bottomRight: {
              border: '1px solid',
              height: 8,
              width: 8,
              right: -4,
              bottom: -4,
              zIndex: zIndex.contractSignatureComponent,
              backgroundColor: white,
            },
          }}
        >
          <Button
            id={component.key}
            onClick={handleComponentClicked}
            ref={draggableButtonRef}
            classes={{
              startIcon: classes.startIcon,
            }}
            className={clsx(classes.eSigComponentButton, {
              [classes.signatureComponent]: isMounted && isClient,
              [classes.eSigComponentValue]: component.value,
              [classes.eSigComponentPlaceholder]:
                !component.value && !component.imageTextValue,
              [classes.signatureButtonCursor]:
                isClient && component.componentType.includes('owner'), // setting cursor to default for internal user components ( client view only )
              [classes.signatureButtonHover]:
                isClient && component.componentType.includes('owner'),
            })}
            style={{
              border:
                // For image-based components, the text content is saved in the "imageTextValue" key
                // For text-based components, the text content is saved in the "value" key
                // If there is content (either image or text), set border to 'none', otherwise, set it to '1px solid'
                isClient && (component.value || component.imageTextValue)
                  ? 'none'
                  : '1px solid ',
            }}
            startIcon={renderSignatureIcon}
          >
            {renderContractComponentValue}
          </Button>
        </Resizable>
        {renderRemoveIcon()}
      </div>
    </Draggable>
  );
});

const areContractDraggableItemPropsEqual = (
  previousProps: ContractDraggableItemProps,
  nextProps: ContractDraggableItemProps,
) => {
  if (previousProps.type !== nextProps.type) return false;
  if (previousProps.component !== nextProps.component) return false;
  if (previousProps.disableDragging !== nextProps.disableDragging) return false;

  if (
    previousProps.type === ContractDragItemType.Edit &&
    nextProps.type === ContractDragItemType.Edit
  ) {
    return (
      previousProps.isComponentSelected === nextProps.isComponentSelected &&
      previousProps.handleComponentClicked ===
        nextProps.handleComponentClicked &&
      previousProps.handleComponentMoved === nextProps.handleComponentMoved &&
      previousProps.handleComponentResized ===
        nextProps.handleComponentResized &&
      previousProps.handleRemoveComponent === nextProps.handleRemoveComponent
    );
  }

  return true;
};

export const ContractDraggableItem = React.memo(
  ContractDraggableItemBase,
  areContractDraggableItemPropsEqual,
);
